import { useState } from "react";
import {
  FileStack,
  ChevronDown,
  ChevronRight,
  Flag,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Mail,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePoRuns, useResolveFlag, type PoRun } from "./use-po-runs";

const PANEL_KEY = "po-organizer-panel-expanded";

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

// Defensive: `flags`/`pos` are JSONB arrays, but a bad agent write can land an
// object ({}) instead of [] — `?? []` doesn't catch that and `.filter`/`.map`
// would throw and crash the whole board. Always coerce to a real array.
function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function openFlags(run: PoRun): number {
  return asArray<{ resolved?: boolean }>(run.flags).filter((f) => !f.resolved).length;
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-2">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div
        className={`text-lg font-semibold ${
          warn && value > 0 ? "text-amber-600" : "text-foreground"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function RunDetailDialog({ run, onClose }: { run: PoRun | null; onClose: () => void }) {
  const resolve = useResolveFlag();
  return (
    <Dialog open={!!run} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        {run && (
          <>
            <DialogHeader>
              <DialogTitle>{run.run_label}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-1">
              Ran {new Date(run.ran_at).toLocaleString()} · {run.new_count} new ·{" "}
              {run.revision_count} revisions · {run.archived_count} archived ·{" "}
              {run.rows_filed} rows filed
            </p>

            {asArray(run.flags).length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Flags
                </div>
                {asArray<PoRun["flags"][number]>(run.flags).map((f) => (
                  <div
                    key={f.id}
                    className={`flex items-start gap-2 rounded-lg border p-3 ${
                      f.resolved
                        ? "border-border bg-muted/30"
                        : "border-amber-200 bg-amber-50 dark:bg-amber-950/20"
                    }`}
                  >
                    <div className="mt-0.5">
                      {f.resolved ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-sm ${
                          f.resolved ? "text-muted-foreground line-through" : "text-foreground"
                        }`}
                      >
                        {f.message}
                      </p>
                      {f.email_url && (
                        <a
                          href={f.email_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                        >
                          <Mail className="w-3 h-3" /> Open email
                        </a>
                      )}
                    </div>
                    {!f.resolved && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resolve.isPending}
                        onClick={() => resolve.mutate({ runId: run.id, flagId: f.id })}
                      >
                        {resolve.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          "Mark resolved"
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                POs filed
              </div>
              {asArray(run.pos).length > 0 ? (
                asArray<PoRun["pos"][number]>(run.pos).map((p, i) => (
                  <div
                    key={`${p.po_number}-${i}`}
                    className="flex items-start justify-between gap-3 rounded-lg border border-border p-2.5"
                  >
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        PO {p.po_number}
                        {p.vendor ? ` · ${p.vendor}` : ""}
                      </div>
                      {p.desc && <div className="text-xs text-muted-foreground">{p.desc}</div>}
                      {(p.tab || p.line_count) && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {p.tab ? `Tab: ${p.tab}` : ""}
                          {p.line_count ? `${p.tab ? " · " : ""}${p.line_count} line${p.line_count > 1 ? "s" : ""}` : ""}
                        </div>
                      )}
                    </div>
                    {p.email_url ? (
                      <a
                        href={p.email_url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        <Mail className="w-3 h-3" /> Email
                      </a>
                    ) : (
                      <span className="shrink-0 text-[11px] text-muted-foreground/60">
                        no email link
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-xs text-muted-foreground">
                  {run.notes || "Per-PO detail not captured for this run."}
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PoOrganizerPanel() {
  const { data, isLoading } = usePoRuns();
  const runs = data ?? [];
  const latest = runs[0] ?? null;
  const needsYou = runs.reduce((n, r) => n + openFlags(r), 0);

  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PANEL_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const openRun = runs.find((r) => r.id === openRunId) ?? null;

  function toggle() {
    setExpanded((v) => {
      const next = !v;
      try {
        localStorage.setItem(PANEL_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  return (
    <div className="border-b bg-background">
      <button
        onClick={toggle}
        className="w-full px-8 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-950/30 flex items-center justify-center">
            <FileStack className="w-4 h-4 text-violet-600" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-foreground">PO organizer</div>
            <div className="text-xs text-muted-foreground">
              {isLoading
                ? "Loading runs…"
                : latest
                ? `Last run ${fmtDate(latest.ran_at)} · ${latest.new_count} new`
                : "No runs yet"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {needsYou > 0 && (
            <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              <Flag className="w-3 h-3" /> {needsYou} needs you
            </span>
          )}
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-8 pb-4">
          {latest && (
            <div className="grid grid-cols-4 gap-2 mb-3">
              <Stat label="New POs" value={latest.new_count} />
              <Stat label="Revisions" value={latest.revision_count} />
              <Stat label="Rows filed" value={latest.rows_filed} />
              <Stat label="Open flags" value={openFlags(latest)} warn />
            </div>
          )}

          {runs.length > 0 ? (
            <div className="rounded-xl border border-border overflow-hidden">
              {runs.map((run) => {
                const of = openFlags(run);
                return (
                  <button
                    key={run.id}
                    onClick={() => setOpenRunId(run.id)}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/40 transition-colors border-b border-border last:border-b-0 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="text-sm text-foreground">{run.run_label}</span>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                      <span>{run.new_count} new</span>
                      {run.revision_count > 0 && <span>· {run.revision_count} rev</span>}
                      {run.rows_filed > 0 && <span>· {run.rows_filed} rows</span>}
                      {of > 0 && <span className="text-amber-600">· {of} flag</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            !isLoading && (
              <p className="text-xs text-muted-foreground py-2">No PO organizer runs yet.</p>
            )
          )}
        </div>
      )}

      <RunDetailDialog run={openRun} onClose={() => setOpenRunId(null)} />
    </div>
  );
}
